import { Component, OnInit, AfterViewInit, ElementRef, ViewChild } from '@angular/core';
import { FormGroup, Validators, FormBuilder, FormArray } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';

import { CampaignsService } from "./campaigns.service";
import { Campaign, CampaignExtraDetails, CampaignPpa } from "./campaign.model";
import { DropdownValue } from '../shared/dropdown.model';
import { AppSettingsService, ClaimsService } from '../core/services';
import { FilterItemsService } from "../shared/filter-items.service";
import { FilterService } from '../core/services';
import { FieldValidatorService } from '../shared/field-validator-service';
import { FormHelperService } from "../core/services/form-helper.service";
import { consts } from "../shared/consts";
import { CustomHorizontalScrollService } from '../shared/custom-horizontal-scroll.service';
import { BrandModel } from '../shared/brand.model';
import { BrandLogo } from '../brand-logos/brand-logo.model';
import { DropdownComponent } from '../index';
import { ObjectFactoryService } from '../core/services/object-factory.service';
import { AdTrailerStatusEnum } from '../brand-statistics/brand-statistics.model';
import { BrandStatisticsService } from '../brand-statistics/brand-statistics.service';

@Component({
    moduleId: module.id,
    templateUrl: 'campaign-details.component.html'
})
export class CampaignDetailsComponent implements OnInit, AfterViewInit {
    countryId: number;
    providerId: number;
    startDate: Date = new Date();
    endDate: Date = new Date();
    deviceTypeId: number;
    gender: number;
    isGridContentType: boolean;

    deviceTypeValues: Array<DropdownValue> = [];
    defaultDeviceTypeSelection: DropdownValue;
    genderValues: Array<DropdownValue> = [];
    defaultGenderSelection: DropdownValue;
    contentTypeValues: Array<DropdownValue> = [];
    defaultContentTypeSelection: DropdownValue;
    providerValues: Array<DropdownValue> = [];
    defaultProviderSelection: DropdownValue = new DropdownValue(-1, '');
    countryValues: Array<DropdownValue> = [];
    defaultCountrySelection: DropdownValue = new DropdownValue(-1, '');

    ppaTypeValues: Array<DropdownValue> = [];

    currentCampaignId: number = 0;
    invalidCampaign: boolean = false;
    mode: string = "Create";
    currentCampaign: Campaign;
    dateError: string;
    campaignForm: FormGroup;
    currentCampaignExtraDetails: CampaignExtraDetails;
    minDate: Date;
    maxDate: Date;
    isLoading: boolean = false;
    showPpasOverlay: boolean = false;
    showAllocationsOverlay: boolean = false;
    showCampaignOverlay: boolean = false;

    brandsList: Array<BrandModel> = [];
    brandId?: number;
    brandLogosList: Array<BrandLogo> = [];
    brandLogoId?: number;
    showBrandOverlay: boolean = false;
    selectedBrand: any;
    brandError: string;

    shouldShowAdTrailer: boolean = false;
    adTrailer: string = '';
    adTrailerStatus: number = AdTrailerStatusEnum.None;
    showAdTrailerOverlay: boolean = false;
    private _unrulyRegEx: RegExp = new RegExp("unruly");
    private _unrulyProviderId: number = consts.UnrulyGridProvider;
    private _youtubeRegEx: RegExp = new RegExp("youtube|youtu");

    isFallback: boolean = false;
    @ViewChild('contentDropdown') contentDropdown: DropdownComponent;
    @ViewChild('genderDropdown') genderDropdown: DropdownComponent;
    @ViewChild('deviceTypeDropdown') deviceTypeDropdown: DropdownComponent;

    constructor(
        private campaignsService: CampaignsService,
        private claimsService: ClaimsService,
        private brandStatisticsService: BrandStatisticsService,
        private formBuilder: FormBuilder,
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private appSettingsService: AppSettingsService,
        private filterItemsService: FilterItemsService,
        private filterService: FilterService,
        private fieldValidatorService: FieldValidatorService,
        private formHelperService: FormHelperService,
        private el: ElementRef,
        private scrollService: CustomHorizontalScrollService,
        private factoryService: ObjectFactoryService) {
    }

    ngOnInit() {
        this.campaignForm = this.formBuilder.group({
            brandField: ['', []],
            productField: ['', []],
            dspField: ['', []],
            sspField: ['', []],
            tradingDeskField: ['', []],
            minAgeField: ['', [this.fieldValidatorService.isHundredIntegerRange]],
            maxAgeField: ['', [this.fieldValidatorService.isHundredIntegerRange]],
            embedCodeField: ['', [Validators.required]],
            hasCompanionField: ['', []],
            totalAllocatedViewsPerUserField: ['', [Validators.required, this.fieldValidatorService.isPositiveInteger]],
            totalAllocatedViewsPerDayField: ['', [Validators.required, this.fieldValidatorService.isPositiveInteger]],
            totalAllocatedViewsField: ['', [Validators.required, this.fieldValidatorService.isPositiveInteger]],
            startDateField: ['', []],
            endDateField: ['', []],
            isAdvertisingIdRequired: ['', []],
            isServerSideField: ['', []],
            isAvailableField: ['', []],
            is18PlusContentField: ['', []],
            isAdvertisingIdRequiredField: ['', []],
            isFallbackCampaign: [{ value: '', disabled: true }, []],
            floorPriceField: ['', [this.fieldValidatorService.isValidDecimal, this.fieldValidatorService.isPositiveNumber]],
            valueField: ['', [Validators.required, this.fieldValidatorService.isValidDecimal, this.fieldValidatorService.isPositiveNumber]],
            brandNameField: ['', []],
            isPrepaidConsumerField: ['', []],
            isPostpaidConsumerField: ['', []],
            isPostpaidCorporateField: ['', []]
        }, { validator: this.fieldValidatorService.isGreatherOrEqualThan('maxAgeField', 'minAgeField') });

        this.deviceTypeValues.push(new DropdownValue(1, 'Android'));
        this.deviceTypeValues.push(new DropdownValue(2, 'IOS'));
        this.deviceTypeValues.push(new DropdownValue(10, 'Other'));

        this.genderValues.push(new DropdownValue(0, 'Male'));
        this.genderValues.push(new DropdownValue(1, 'Female'));
        this.genderValues.push(new DropdownValue(2, 'Both'));

        this.contentTypeValues.push(new DropdownValue(0, 'Grid'));
        this.contentTypeValues.push(new DropdownValue(1, 'Mediation'));

        this.providerValues = this.filterItemsService.getNonConfigurableProviders(false);
        this.countryValues = this.filterItemsService.getCountries(false);

        this.startDate = new Date();
        this.endDate = new Date();

        this.currentCampaignId = this.activatedRoute.snapshot.params['id'];
        this.currentCampaignExtraDetails = new CampaignExtraDetails();
        this.currentCampaignExtraDetails.campaignPpa = [];
        this.currentCampaignExtraDetails.publisherCampaignAllocations = [];

        this.ppaTypeValues.push(new DropdownValue(1, 'Rate and Share'));
        this.ppaTypeValues.push(new DropdownValue(2, 'Email Capture'));
        this.ppaTypeValues.push(new DropdownValue(3, 'Survey'));

        this.brandsList = this.filterItemsService.getBrands();
    }

    ngAfterViewInit() {
        this.defaultDeviceTypeSelection = this.deviceTypeValues[2];
        this.defaultGenderSelection = this.genderValues[2];
        this.defaultContentTypeSelection = this.contentTypeValues[1];
        this.defaultProviderSelection = this.filterItemsService.getDefaultNonConfigurableProviderSelection(false);
        this.defaultCountrySelection = this.filterItemsService.getDefaultCountrySelection(false);

        this.countryId = this.defaultCountrySelection.value;
        this.providerId = this.defaultProviderSelection.value;
        this.deviceTypeId = this.defaultDeviceTypeSelection.value;
        this.gender = this.defaultGenderSelection.value;
        this.isGridContentType = this.defaultContentTypeSelection.value == 0;

        if (this.currentCampaignId > 0) {
            this.mode = " Edit";
            this.isLoading = true;
            this.showCampaignOverlay = true;
            this.showAllocationsOverlay = true;
            this.showPpasOverlay = true;
            this.campaignsService.getCamapign(this.currentCampaignId).subscribe(respose => {
                let currentCampaign = respose.json();
                if (!currentCampaign) {
                    this.invalidCampaign = true;
                    return;
                }
                else {
                    this.currentCampaign = currentCampaign;
                    if (this.currentCampaign) {
                        //POSTPONED
                        // //if the campaign can be shown in the selector (unruly for selector, youtube) -- vast is handled through brand engine
                        // if (this.currentCampaign.providerId == this._unrulyProviderId && this._unrulyRegEx.test(this.currentCampaign.embedCode.toLowerCase())) {
                        //     this.showAdTrailerSection(this.currentCampaign);
                        // } else 
                        if (this._youtubeRegEx.test(this.currentCampaign.embedCode.toLowerCase())) {
                            this.showAdTrailerSection(this.currentCampaign);
                        }
                        this.resetCampaignFields(this.currentCampaign);
                        this.bindCampaignForm(this.currentCampaign);

                        this.minDate = this.startDate;
                        this.maxDate = this.endDate;
                        this.isLoading = true;
                        this.campaignsService.getCamapignExtraDetails(this.currentCampaignId).subscribe(response => {
                            let result = response.json();
                            this.currentCampaignExtraDetails.publisherCampaignAllocations = this.campaignsService.getPublisherAllocations(result);
                            this.currentCampaignExtraDetails.campaignPpa = this.campaignsService.getPpas(result);
                        },
                            () => { },
                            () => {
                                this.isLoading = false;
                                this.toggleHorizontalScroll();
                            });

                        this.isLoading = true;
                        this.getBrandAndLogoForCurrentGridCampaign();
                    }
                }
            },
                () => { },
                () => {
                    this.isLoading = false;
                    this.showCampaignOverlay = false;
                    this.showAllocationsOverlay = false;
                    this.showPpasOverlay = false;
                })
        } else {
            let campaign = this.campaignsService.getDuplicatedCampaign();
            if (campaign) {
                this.resetCampaignFields(campaign);

                //if fallback, gender, device type,floor price, value, max age, min age, allocated views should have default un-editable values
                //and campaign type is mediation, and isFallback flag is set and shown
                if (this.isFallback) {
                    // this.defaultContentTypeSelection = this.contentTypeValues[1];
                    this.defaultGenderSelection = this.genderValues[2];
                    this.defaultDeviceTypeSelection = this.deviceTypeValues[2];
                    campaign.minAge = consts.DefaultMinAge;
                    campaign.maxAge = consts.DefaultMaxAge;
                    campaign.totalAllocatedViewsPerUser = consts.DefaultTotalAllocatedViewsPerUser;
                    campaign.totalAllocatedViewsPerDay = consts.DefaultTotalAllocatedViewsPerDay;
                    campaign.totalAllocatedViews = consts.DefaultTotalAllocatedViews;
                    campaign.floorPrice = consts.DefaultFloorPrice;
                    campaign.value = consts.DefaultValue;
                }

                this.bindCampaignForm(campaign);
            }
            // this.isLoading = true;       
        }
    }

    private showAdTrailerSection(campaign: Campaign) {
        this.shouldShowAdTrailer = true;
        this.brandStatisticsService.getAdtrailerInfo(campaign.embedCode.toLowerCase())
            .subscribe((response) => {
                let result = response.json();
                if (result != null) {
                    this.adTrailerStatus = result.adTrailerStatus;
                    if (this.adTrailerStatus != AdTrailerStatusEnum.None)
                        this.adTrailer = result.adTrailerUrl;
                }
            }, () => { }, () => { this.showAdTrailerOverlay = false; });
    }

    private resetCampaignFields(campaign: Campaign) {
        this.defaultDeviceTypeSelection = this.getDeviceType(campaign.isAndroid);
        this.defaultGenderSelection = this.getGender(campaign.isMale);

        if (campaign.isGridContentType)
            this.defaultContentTypeSelection = this.contentTypeValues[0];
        else
            this.defaultContentTypeSelection = this.contentTypeValues[1];

        this.defaultProviderSelection = this.getProvider(campaign.providerId);
        this.defaultCountrySelection = this.getCountry(campaign.countryId);

        this.countryId = campaign.countryId;
        this.providerId = campaign.providerId;
        this.deviceTypeId = this.defaultDeviceTypeSelection.value;
        this.gender = this.defaultGenderSelection.value;

        //check if provider is fallback and set checkbox in ui
        let currentProvider = this.filterService.getProviders().filter(x => x.id == this.providerId)[0];
        this.isFallback = currentProvider.isFallback;

        this.isGridContentType = this.defaultContentTypeSelection.value == 0;

        this.startDate = new Date(campaign.startDateUtc);
        this.endDate = new Date(campaign.endDateUtc);
    }

    private getBrandAndLogoForCurrentGridCampaign() {
        if (this.isGridContentType) {
            if (this.currentCampaign.brandLogoId) {
                this.showBrandOverlay = true;
                this.campaignsService.getBrandForLogo(this.currentCampaign.brandLogoId).subscribe(response => {
                    let result = response.json();
                    //set the selected brand
                    //get all the associated logos for the selected brand
                    this.resetBrandAndLogo(result);
                    //set the selected brand logo
                    this.brandLogoId = this.currentCampaign.brandLogoId;
                },
                    () => {
                        this.showAllocationsOverlay = false;
                        this.showBrandOverlay = false;
                        this.showCampaignOverlay = false;
                    },
                    () => {
                        this.showAllocationsOverlay = false;
                        this.showCampaignOverlay = false;
                    });
            }
        }
        else {
            this.isLoading = false;
            this.showAllocationsOverlay = false;
            this.showCampaignOverlay = false;
        }
    }

    isSambaAdmin(): boolean {
        return this.claimsService.isSambaAdmin();
    }

    showErrorMessage(controlName: string, validator: string): boolean {
        return this.formHelperService.showErrorMessage(this.campaignForm, controlName, validator)
    }

    onSaveChanges() {
        if (!this.isSambaAdmin()) {
            this.defaultContentTypeSelection = this.contentTypeValues[1];
        }
        
        if (this.startDate > this.endDate) {
            this.dateError = consts.StartDateGreaterThanEndDateText;
            return;
        }
        this.dateError = '';
        if (this.isGridContentType)
            if (this.brandLogoId == null) {
                this.brandError = consts.BrandInfoNotSelectedText;
                return;
            }
        this.brandError = '';

        if (!this.currentCampaign) {
            let campaign: Campaign = new Campaign();
            campaign = this.bindCampaign();
            this.isLoading = true;
            this.showCampaignOverlay = true;
            this.campaignsService.createCampaign(campaign).subscribe(respose => {
                let campaignId = respose.json().id;
                this.router.navigate([this.appSettingsService.CampaignDetails, campaignId]);
                //POSTPONED
                // //if the campaign can be shown in the selector (unruly for selector, youtube) -- vast is handled through brand engine
                // if (campaign.providerId == this._unrulyProviderId && this._unrulyRegEx.test(campaign.embedCode.toLowerCase())) {
                //     this.showAdTrailerSection(campaign);
                // } else 
                if (this._youtubeRegEx.test(campaign.embedCode.toLowerCase())) {
                    this.showAdTrailerSection(campaign);
                }
            },
                () => { this.isLoading = false; this.showCampaignOverlay = false; },
                () => { this.isLoading = false; this.showCampaignOverlay = false; });
        }
        else {
            this.currentCampaign = this.bindCampaign();
            this.isLoading = true;
            this.showCampaignOverlay = true;
            this.showAllocationsOverlay = true;
            this.showPpasOverlay = true;
            this.campaignsService.updateCampaign(this.currentCampaign).subscribe(
                response => {
                    //refresh publisher params
                    let result = response.json();
                    this.bindCampaignForm(result);
                    this.currentCampaignExtraDetails.publisherCampaignAllocations = [];
                    this.currentCampaignExtraDetails.campaignPpa = [];
                    this.campaignsService.getCamapignExtraDetails(this.currentCampaignId).subscribe(response => {
                        let result = response.json();
                        this.currentCampaignExtraDetails.publisherCampaignAllocations = this.campaignsService.getPublisherAllocations(result);
                        this.currentCampaignExtraDetails.campaignPpa = this.campaignsService.getPpas(result);
                    }, () => { }, () => {
                        this.showAllocationsOverlay = false;
                        this.showPpasOverlay = false;
                        this.toggleHorizontalScroll();
                    });
                    //POSTPONED
                    // //if the campaign can be shown in the selector (unruly for selector, youtube) -- vast is handled through brand engine
                    // if (this.currentCampaign.providerId == this._unrulyProviderId && this._unrulyRegEx.test(this.currentCampaign.embedCode.toLowerCase())) {
                    //     this.showAdTrailerSection(this.currentCampaign);
                    // } else 
                    if (this._youtubeRegEx.test(this.currentCampaign.embedCode.toLowerCase())) {
                        this.showAdTrailerSection(this.currentCampaign);
                    }
                },
                () => { this.isLoading = false; this.showCampaignOverlay = false; },
                () => { this.isLoading = false; this.showCampaignOverlay = false; });
        }
    }

    private bindCampaign(): Campaign {
        return this.factoryService.createCampaign(
            this.currentCampaign == null ? 0 : this.currentCampaign.id, this.campaignForm.value.embedCodeField, !this.campaignForm.value.hasCompanionField ? false : true,
            this.campaignForm.value.brandField, this.campaignForm.value.productField, this.campaignForm.value.totalAllocatedViewsField,
            this.campaignForm.value.totalAllocatedViewsPerDayField, this.campaignForm.value.totalAllocatedViewsPerUserField,
            this.campaignForm.value.minAgeField, this.campaignForm.value.maxAgeField, this.countryId, this.providerId,
            new Date(Date.UTC(this.startDate.getFullYear(), this.startDate.getMonth(), this.startDate.getDate())),
            new Date(Date.UTC(this.endDate.getFullYear(), this.endDate.getMonth(), this.endDate.getDate())),
            this.deviceTypeId == 1 ? true : this.deviceTypeId == 2 ? false : null, this.gender == 0 ? true : this.gender == 1 ? false : null,
            !this.campaignForm.value.isAvailableField ? false : true, !this.campaignForm.value.is18PlusContentField ? false : true,
            this.isGridContentType, !this.campaignForm.value.isAdvertisingIdRequiredField ? false : true, this.campaignForm.value.floorPriceField,
            this.campaignForm.value.valueField, this.campaignForm.value.dspField, this.campaignForm.value.sspField, this.campaignForm.value.tradingDeskField,
            this.brandLogoId, !this.campaignForm.value.isServerSideField ? false : true,
            !this.campaignForm.value.isPrepaidConsumerField ? false : true, !this.campaignForm.value.isPostpaidConsumerField ? false : true, !this.campaignForm.value.isPostpaidCorporateField ? false : true
        );
    }

    private bindCampaignForm(campaign: Campaign) {
        this.campaignForm.controls["brandField"].setValue(campaign.brand);
        this.campaignForm.controls["productField"].setValue(campaign.product);
        this.campaignForm.controls["dspField"].setValue(campaign.dsp);
        this.campaignForm.controls["sspField"].setValue(campaign.ssp);
        this.campaignForm.controls["tradingDeskField"].setValue(campaign.tradingDesk);
        this.campaignForm.controls["embedCodeField"].setValue(campaign.embedCode);
        this.campaignForm.controls["hasCompanionField"].setValue(campaign.hasCompanion);
        this.campaignForm.controls["isAvailableField"].setValue(campaign.isAvailable);
        this.campaignForm.controls["isServerSideField"].setValue(campaign.isServerSide);
        this.campaignForm.controls["is18PlusContentField"].setValue(campaign.is18PlusContent);
        this.campaignForm.controls["isAdvertisingIdRequiredField"].setValue(campaign.isAdvertisingIdRequired);
        this.campaignForm.controls["totalAllocatedViewsField"].setValue(campaign.totalAllocatedViews);
        this.campaignForm.controls["totalAllocatedViewsPerDayField"].setValue(campaign.totalAllocatedViewsPerDay);
        this.campaignForm.controls["totalAllocatedViewsPerUserField"].setValue(campaign.totalAllocatedViewsPerUser);
        this.campaignForm.controls["minAgeField"].setValue(campaign.minAge);
        this.campaignForm.controls["maxAgeField"].setValue(campaign.maxAge);
        this.campaignForm.controls["valueField"].setValue(campaign.value);
        this.campaignForm.controls["floorPriceField"].setValue(campaign.floorPrice);
        this.campaignForm.controls["isFallbackCampaign"].setValue(this.isFallback);
        this.campaignForm.controls["isPrepaidConsumerField"].setValue(campaign.isPrePaidConsumer);
        this.campaignForm.controls["isPostpaidConsumerField"].setValue(campaign.isPostPaidConsumer);
        this.campaignForm.controls["isPostpaidCorporateField"].setValue(campaign.isPostPaidCorporate);
    }

    onDeviceTypeValueChanged(item) {
        this.deviceTypeId = item.value;
    }

    onGenderValueChanged(item) {
        this.gender = item.value;
    }

    onContentTypeValueChanged(item) {
        this.isGridContentType = item.value == 0;
        //if not grid, remove all brand info
        if (!this.isGridContentType) {
            this.brandId = null;
            this.brandLogoId = null;
            this.selectedBrand = null;
        }
    }

    onProviderValueChanged(item) {
        this.providerId = item.value;
        let selectedProvider = this.filterService.getProviders().filter(x => x.id == this.providerId)[0];
        this.isFallback = selectedProvider.isFallback;
        //if fallback, gender, device type,floor price, value, max age, min age, allocated views should have default un-editable values
        //and campaign type is mediation, and isFallback flag is set and shown
        if (this.isFallback) {
            this.campaignForm.controls["isFallbackCampaign"].setValue(this.isFallback);
            this.campaignForm.controls["totalAllocatedViewsField"].setValue(consts.DefaultTotalAllocatedViews);
            this.campaignForm.controls["totalAllocatedViewsPerDayField"].setValue(consts.DefaultTotalAllocatedViewsPerDay);
            this.campaignForm.controls["totalAllocatedViewsPerUserField"].setValue(consts.DefaultTotalAllocatedViewsPerUser);
            this.campaignForm.controls["minAgeField"].setValue(consts.DefaultMinAge);
            this.campaignForm.controls["maxAgeField"].setValue(consts.DefaultMaxAge);
            this.campaignForm.controls["valueField"].setValue(consts.DefaultValue);
            this.campaignForm.controls["floorPriceField"].setValue(consts.DefaultFloorPrice);
            // this.defaultContentTypeSelection = this.contentTypeValues[1];
            this.defaultGenderSelection = this.genderValues[2];
            this.defaultDeviceTypeSelection = this.deviceTypeValues[2];
            // this.contentDropdown.setCurrentSelection(this.contentTypeValues[1]);
            this.genderDropdown.setCurrentSelection(this.genderValues[2]);
            this.deviceTypeDropdown.setCurrentSelection(this.deviceTypeValues[2]);
            // this.isGridContentType = this.defaultContentTypeSelection.value == 0;
        }
    }

    onCountryValueChanged(item) {
        this.countryId = item.value;
    }

    onDuplicateCampaign(evemt) {
        let campaign: Campaign = new Campaign();
        campaign.brand = this.campaignForm.value.brandField + ' copy';
        campaign.product = this.campaignForm.value.productField + ' copy';
        campaign.embedCode = this.campaignForm.value.embedCodeField;
        campaign.hasCompanion = !this.campaignForm.value.hasCompanionField ? false : true;
        campaign.isAvailable = !this.campaignForm.value.isAvailableField ? false : true;
        campaign.isServerSide = !this.campaignForm.value.isServerSideField ? false : true;
        campaign.is18PlusContent = !this.campaignForm.value.is18PlusContentField ? false : true;
        campaign.isAdvertisingIdRequired = !this.campaignForm.value.isAdvertisingIdRequiredField ? false : true;
        campaign.brandLogoId = this.brandLogoId;
        campaign.startDateUtc = new Date();
        campaign.endDateUtc = new Date();
        campaign.isPrePaidConsumer = !this.campaignForm.value.isPrepaidConsumerField ? false : true;
        campaign.isPostPaidConsumer = !this.campaignForm.value.isPostpaidConsumerField ? false : true;
        campaign.isPostPaidCorporate = !this.campaignForm.value.isPostpaidCorporateField ? false : true;

        if (this.isFallback == false) {
            campaign.minAge = this.campaignForm.value.minAgeField;
            campaign.maxAge = this.campaignForm.value.maxAgeField;
            campaign.totalAllocatedViewsPerUser = this.campaignForm.value.totalAllocatedViewsPerUserField;
            campaign.totalAllocatedViewsPerDay = this.campaignForm.value.totalAllocatedViewsPerDayField;
            campaign.totalAllocatedViews = this.campaignForm.value.totalAllocatedViewsField;
            campaign.floorPrice = this.campaignForm.value.floorPriceField;
            campaign.value = this.campaignForm.value.valueField;
        } else {
            campaign.minAge = consts.DefaultMinAge;
            campaign.maxAge = consts.DefaultMaxAge;
            campaign.totalAllocatedViewsPerUser = consts.DefaultTotalAllocatedViewsPerUser;
            campaign.totalAllocatedViewsPerDay = consts.DefaultTotalAllocatedViewsPerDay;
            campaign.totalAllocatedViews = consts.DefaultTotalAllocatedViews;
            campaign.floorPrice = consts.DefaultFloorPrice;
            campaign.value = consts.DefaultValue;
        }

        campaign.countryId = this.countryId;
        campaign.providerId = this.providerId;
        campaign.isAndroid = this.deviceTypeId == 1 ? true : this.deviceTypeId == 2 ? false : null;
        campaign.isMale = this.gender == 0 ? true : this.gender == 1 ? false : null;
        campaign.isGridContentType = this.isGridContentType;

        this.campaignsService.duplicateCampaign(campaign);
        this.router.navigate([this.appSettingsService.CampaignDetails]);
    }

    onGenerateAdTrailerClick(event) {
        this.showAdTrailerOverlay = true;
        this.brandStatisticsService.requestNewAdTrailerForMediaFile(this.currentCampaign.embedCode)
            .subscribe(response => { this.adTrailerStatus = AdTrailerStatusEnum.Pending; },
                () => { }, () => { this.showAdTrailerOverlay = false; });
    }

    onResize(event) {
        this.toggleHorizontalScroll();
    }

    private toggleHorizontalScroll() {
        let self = this;
        setTimeout(function () {
            self.scrollService.toggleHorizontalScroll();
        }, 10);
    }

    private getDeviceType(isAndroid: Boolean): DropdownValue {
        if (isAndroid === true)
            return this.deviceTypeValues[0];
        if (isAndroid === false)
            return this.deviceTypeValues[1];
        return this.deviceTypeValues[2];
    }

    private getGender(isMale: Boolean): DropdownValue {
        if (isMale === true)
            return this.genderValues[0];
        if (isMale === false)
            return this.genderValues[1];
        return this.genderValues[2];
    }

    private getProvider(providerId: number): DropdownValue {
        for (let item of this.providerValues)
            if (item.value === providerId)
                return item;

        if (this.providerValues.length > 0)
            return this.providerValues[0];
        return null;
    }

    private getCountry(countryId: number): DropdownValue {
        for (let item of this.countryValues)
            if (item.value === countryId)
                return item;

        if (this.countryValues.length > 0)
            return this.countryValues[0];
        return null;
    }

    private refreshCampaignDetails() {
        this.campaignsService.getCamapign(this.currentCampaignId).subscribe(respose => {
            let currentCampaign = respose.json();
            if (!currentCampaign) {
                this.invalidCampaign = true;
                return;
            }
            else {
                this.currentCampaign = currentCampaign;
                if (this.currentCampaign) {
                    this.resetCampaignFields(this.currentCampaign);

                    this.bindCampaignForm(this.currentCampaign);

                    this.minDate = this.startDate;
                    this.maxDate = this.endDate;
                    this.isLoading = true;
                    this.campaignsService.getCamapignExtraDetails(this.currentCampaignId).subscribe(response => {
                        let result = response.json();
                        this.currentCampaignExtraDetails.publisherCampaignAllocations = this.campaignsService.getPublisherAllocations(result);
                        this.currentCampaignExtraDetails.campaignPpa = this.campaignsService.getPpas(result);
                    }, () => { },
                        () => {
                            this.isLoading = false;
                            this.toggleHorizontalScroll();
                        });

                    this.isLoading = true;

                    this.getBrandAndLogoForCurrentGridCampaign();
                }
            }
        },
            () => { },
            () => {
                this.isLoading = false;
                this.showCampaignOverlay = false;
                this.showAllocationsOverlay = false;
            });
    }

    onPublisherAllocationCreated(item) {
        this.showCampaignOverlay = true;
        this.isLoading = true;
        this.showAllocationsOverlay = true;

        item.sspName = this.campaignsService.getPublisherName(this.filterService.getPublishers(), item.sspId);
        this.currentCampaignExtraDetails.publisherCampaignAllocations.push(item);

        this.refreshCampaignDetails();
    }

    onPublisherAllocationChanged(item) {
        this.showCampaignOverlay = true;
        this.isLoading = true;
        this.showAllocationsOverlay = true;

        let publisherCampaignAllocation = this.currentCampaignExtraDetails.publisherCampaignAllocations.find(s => s.id === item.id)
        if (publisherCampaignAllocation) {
            publisherCampaignAllocation.endUtc = item.endUtc;
            publisherCampaignAllocation.isAvailable = item.isAvailable;
            publisherCampaignAllocation.publisherCpe = item.publisherCpe;
            publisherCampaignAllocation.publisherId = item.publisherId;
            publisherCampaignAllocation.publisherName = item.publisherName;
            publisherCampaignAllocation.startUtc = item.startUtc;
            publisherCampaignAllocation.totalAllocatedViews = item.totalAllocatedViews;
            publisherCampaignAllocation.totalAllocatedViewsPerDay = item.totalAllocatedViewsPerDay;
            publisherCampaignAllocation.totalRequestsPerUserPerDay = item.totalRequestsPerUserPerDay;
        }

        this.refreshCampaignDetails();
    }

    onPpaCreated(item) {
        this.currentCampaignExtraDetails.campaignPpa.push(item);
    }

    onPpaChanged(item) {
        let campaignPpa = this.currentCampaignExtraDetails.campaignPpa.find(s => s.id === item.id);
        if (campaignPpa) {
            campaignPpa.displayProbability = item.displayProbability;
            campaignPpa.order = item.order;
            campaignPpa.isLive = item.isLive;
        }
    }

    onDeletePpa(ppaId: number) {
        if (confirm('Are you sure you want to delete this item?')) {
            this.campaignsService.deletePpa(ppaId).subscribe(response => {
                if (this.currentCampaignExtraDetails && this.currentCampaignExtraDetails.campaignPpa) {
                    let index = this.currentCampaignExtraDetails.campaignPpa.findIndex(s => s.id === ppaId);
                    if (index !== -1)
                        this.currentCampaignExtraDetails.campaignPpa.splice(index, 1);
                    return;
                }
            });
        }
    }

    onDeletePublisherAllocation(publisherAllocationId: number) {
        if (confirm('Are you sure you want to delete this item?')) {
            this.isLoading = true;
            this.showCampaignOverlay = true;
            this.showAllocationsOverlay = true;
            this.campaignsService.deletePublisherAllocation(publisherAllocationId).subscribe(response => {
                if (this.currentCampaignExtraDetails && this.currentCampaignExtraDetails.publisherCampaignAllocations) {
                    let index = this.currentCampaignExtraDetails.publisherCampaignAllocations.findIndex(s => s.id === publisherAllocationId);
                    if (index !== -1)
                        this.currentCampaignExtraDetails.publisherCampaignAllocations.splice(index, 1);
                    return;
                }
            },
                () => { },
                () => {
                    this.isLoading = false;
                    this.showCampaignOverlay = false;
                    this.showAllocationsOverlay = false;
                });
        }
    }

    getPpaAvailableTypes(): Array<DropdownValue> {
        let result = new Array<DropdownValue>();

        for (let item of this.ppaTypeValues) {
            var index = this.currentCampaignExtraDetails.campaignPpa.findIndex(s => s.ppaType == item.value);
            if (index === -1)
                result.push(item);
        }
        return result;
    }

    onEndDateChanged(newDate) {
        this.maxDate = newDate;
    }

    onBrandAutocompleteClicked(item) {
        let ng2Autocomplete = this.el.nativeElement.querySelectorAll("ng2-auto-complete");
        $(ng2Autocomplete).addClass('custom-autocomplete');
        //remove the selection from the previous brand and select the current brand
        let listOfBrands = $(ng2Autocomplete).find('ul li');
        let brand = this.selectedBrand;
        $(listOfBrands).each(function (i, e) {
            $(e).removeClass('selected');
            if (brand == $(e).text()) $(e).addClass('selected');
        });
    }

    onBrandValueChanged(item) {
        this.resetBrandAndLogo(item);
    }

    private resetBrandAndLogo(item) {
        this.showBrandOverlay = true;
        this.brandId = item.id;
        this.brandLogosList = [];
        this.campaignsService.getBrandLogosForBrand(this.brandId).subscribe(response => {
            let list = response.json();
            if (list) {
                for (let item of list) {
                    this.brandLogosList.push(item);
                }
                if (this.currentCampaign && this.currentCampaign.brandLogoId) {
                    this.brandLogoId = this.currentCampaign.brandLogoId;
                    this.selectedBrand = item.name;
                }
            }
        }, () => { }, () => { this.isLoading = false; this.showBrandOverlay = false; });
    }

    onBrandLogoSelection(item) {
        this.brandLogoId = item.id;
    }

    autocompleListFormatter(data: any): string {
        return `${data.name}`;
    }

    public onDateClicked(event) {
        let ng2datePicker = this.el.nativeElement.querySelectorAll("ng2-datetime-picker[draggable='true']");
        $(ng2datePicker).attr('draggable', 'false');
    }
}


// WEBPACK FOOTER //
// ./src/app/campaigns/campaign-details.component.ts